import React, { useState } from 'react';
import styled from 'styled-components';
import { CommonButton } from '../../../Styles/CommonStyles';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import nameStringFn from '../../../data/nameStringFn';
import { useUpdateComplaintsMutation } from '../../../features/complaints/complaintsApiSlice';

const FormContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 100%;
`;

const FormTitle = styled.h3`
  margin-bottom: 20px;
  color: #1976d2;
  font-size: 1.25rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: flex-start;
`;

const FormField = styled.div`
  flex: 1;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
`;

const SubmitButton = styled(Button)`
  background-color: #4caf50;
`;

const CancelButton = styled(Button)`
  background-color: #f44336;
`;

const SupportAdminComplaintForm = ({ 
  row, 
  remark, 
  setRemark, 
  auth, 
  user_current_office, 
  getSingleuser, 
  supportusers,
  handleBack, 
  availableActions 
}) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [updateComplaint] = useUpdateComplaintsMutation();

  const handleActionSubmit = async () => {
    if (!selectedAction) {
      alert('Please select an action');
      return;
    }

    if (!remark.trim()) {
      alert('Please enter a remark');
      return;
    }

    setLoading(true);
    
    try {
      const actionData = availableActions.find(action => action.code === selectedAction);
      const nextAssignee = getNextAssignee(actionData);

      if (!nextAssignee) {
        setLoading(false);
        alert('Error: Unable to determine next assignee. Please contact administrator.');
        return;
      }

      if (!nextAssignee.id) {
        setLoading(false);
        alert('Error: Invalid assignee data. Please contact administrator.');
        return;
      }

      const updateData = {
        id: row._id,
        req_data_entry: {
          action: actionData.action,
          sent_to_tag: actionData.sent_to_tag,
          text: remark,
          sent_by: {
            id: auth.id,
            name: getSingleuser[auth.id]?.name || {},
            designation: getSingleuser[auth.id]?.designation?.[row.store] || 'L1Support'
          },
          sent_to: nextAssignee,
          time_stamp: new Date().toISOString()
        },
        curr_status: actionData.updatedStatus,
        curr_sent_by: auth.id,
        curr_sent_to: nextAssignee.id,
        curr_sent_to_string: `${nameStringFn(nextAssignee)}`.trim(),
        curr_sent_to_tag: actionData.sent_to_tag,
        opened: actionData.code === 'L1CLOSE'
      }; 

      if (actionData.code === 'L1CLOSE') {
        updateData.closed_at = new Date().toISOString();
      }

      await updateComplaint(updateData).unwrap();
      alert('Action completed successfully');
      handleBack();
    } catch (error) {
      console.error('Error updating complaint:', error);
      alert('Failed to submit action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getNextAssignee = (actionData) => {
    if (!row.req_data || row.req_data.length === 0) {
      console.error('No req_data found in complaint');
      return null;
    }

    switch (actionData.code) {
      case 'L1CLOSE': // Close Complaint - notify original user
        return row.req_data[0]?.sent_by || null;
      
      case 'L1FWDSUP': { // Return to Supervisor for Action
        const supervisor = supportusers
          ? Object.values(supportusers).find(user => user.roles?.includes('Supervisor'))
          : null;
        return supervisor ? {
          id: supervisor._id || supervisor.id,
          name: supervisor.name || {},
          designation: 'Supervisor'
        } : null;
      }
      
      default:
        return null;
    }
  }; 

  return (
    <FormContainer>
      <FormTitle>L1/L2 Support Actions</FormTitle>
      
      <FormRow>
        <FormField>
          <FormControl fullWidth>
            <InputLabel>Select Action</InputLabel>
            <Select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              label="Select Action"
            >
              {availableActions.map((action) => (
                <MenuItem key={action.code} value={action.code}>
                  {action.action}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Enter your remarks here..."
            variant="outlined"
          />
        </FormField>
      </FormRow>

      <ButtonsWrapper>
        <SubmitButton
          onClick={handleActionSubmit}
          disabled={loading || !selectedAction || !remark.trim()}
        >
          {loading ? 'Submitting...' : 'Submit Action'}
        </SubmitButton>
        <CancelButton onClick={handleBack}>
          Cancel
        </CancelButton>
      </ButtonsWrapper>
    </FormContainer>
  );
};

export default SupportAdminComplaintForm;
